'use strict';

function StripeServiceFactory($window, StripeRepo) {
  const Stripe = $window.Stripe;
  let stripeInstance = null;
  let key;

  function initStripe() {
    StripeRepo.getKey().$promise.then(res => {
      key = res.key;
      if(key && Stripe) {
        stripeInstance = Stripe(key);
      }
    });
  }

  initStripe();

  function addTokenToUser(token) {
    return StripeRepo.newSource({ token }).$promise;
  }

  function getClientId() {
    return StripeRepo.getClientId().$promise.then(res => res.clientId);
  }

  function connectStripeGroup(token, groupId) {
    return StripeRepo.connectStripeGroup({ token, groupId }).$promise
      .then(res => res.data || []);
  }

  //////////////////////////////////////////////////////////////////////////////
  // Payment Methods (Cards & Bank Accounts)
  //////////////////////////////////////////////////////////////////////////////
  function indexUserPaymentMethods() {
    return StripeRepo.getPaymentMethods().$promise.then(res => res || []);
  }

  function indexUserCards() {
    return StripeRepo.getCards().$promise.then(res => res.data || []);
  }

  // Stripe.js v3: Use Elements and createPaymentMethod for cards
  async function createCardPaymentMethod(cardElement, billingDetails) {
    if(!stripeInstance) {
      console.error('Stripe not initialized in createCardPaymentMethod');
      throw new Error('Stripe not initialized');
    }
    if(!cardElement) {
      console.error('Stripe cardElement is not provided');
      throw new Error('Card element not mounted');
    }
    const result = await stripeInstance.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billingDetails
    });
    return result;
  }

  // Attach a PaymentMethod to a customer
  function attachPaymentMethodToUser(paymentMethodId) {
    return StripeRepo.attachPaymentMethodToUser({paymentMethodId}).$promise;
  }

  // Update PaymentMethod metadata
  function updateCard(paymentMethodId, updates) {
    return StripeRepo.updateCard(paymentMethodId, updates).$promise;
  }

  function deleteCard(cardId) {
    return StripeRepo.deleteCard({ cardId }).$promise;
  }

  // Create a PaymentIntent
  function createPaymentIntent(paymentIntentData) {
    return StripeRepo.createPaymentIntent(paymentIntentData).$promise;
  }

  //////////////////////////////////////////////////////////////////////////////
  // Accounts
  //////////////////////////////////////////////////////////////////////////////
  function indexUserAccounts() {
    return StripeRepo.getAccounts().$promise.then(res => res.data || []);
  }

  function createAccountToken(account, cb) {
    // convert all account properties to strings
    for(const accKey in account) {
      if(
        Object.prototype.hasOwnProperty.call(account, accKey)
        && typeof account[accKey] !== 'string'
      ) {
        account[accKey] = String(account[accKey]);
      }
    }
    stripeInstance.createToken('bank_account', account).then(cb);
  }

  // Create SetupIntent for bank account verification (modern approach)
  function createBankAccountSetupIntent(params) {
    return StripeRepo.createBankAccountSetupIntent(params || {}).$promise
      .then(res => res || {});
  }

  // Verify bank account (for us_bank_account)
  function verifyAccount(paymentMethodId, amounts, descriptorCode) {
    return StripeRepo
      .verifyAccount({ paymentMethodId, amounts, descriptorCode }).$promise
      .then(res => {
        return res || {};
      });
  }

  function getAccountVerifyMode(paymentMethodId) {
    return StripeRepo.getAccountVerifyMode({ paymentMethodId }).$promise
      .then(res => res || {});
  }

  function deleteAccount(accountId) {
    return StripeRepo.deleteAccount({ accountId }).$promise;
  }

  function deleteBankAccount(paymentMethodId, setupIntentId) {
    const params = { paymentMethodId };
    if(setupIntentId) {
      params.setupIntentId = setupIntentId;
    }
    return StripeRepo.deleteBankAccount(params).$promise;
  }

  function getStripeInstance() {
    return stripeInstance;
  }

  // Handle 3D Secure authentication via Stripe.js
  async function handlePaymentAuthentication(clientSecret) {
    if(!stripeInstance) {
      throw new Error('Stripe not initialized');
    }
    if(!clientSecret) {
      throw new Error('Client secret required for authentication');
    }
    try {
      const result = await stripeInstance.handleNextAction({clientSecret});
      if(result.error) {
        throw result.error;
      }
      // result.paymentIntent contains the updated PaymentIntent
      // after authentication.
      return result.paymentIntent;
    } catch(e) {
      console.error('3DS Authentication error:', e);
      throw e;
    }
  }

  // Return the service object
  return {
    addTokenToUser,
    getClientId,
    connectStripeGroup,
    indexUserPaymentMethods,
    indexUserCards,
    createCardPaymentMethod,
    attachPaymentMethodToUser,
    updateCard,
    deleteCard,
    createPaymentIntent,
    indexUserAccounts,
    createAccountToken,
    createBankAccountSetupIntent,
    getAccountVerifyMode,
    verifyAccount,
    deleteAccount,
    deleteBankAccount,
    getStripeInstance,
    handlePaymentAuthentication
  };
}

StripeServiceFactory.$inject = ['$window', 'StripeRepo'];
export default StripeServiceFactory;
